import React from "react";
import classNames from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import { getSpotConvertPathForDisplaySymbol } from "@/components/Pages/Convert/convertResultShared";
import { futuresPathFromSymbol } from "@/components/Pages/AssetsTrading/utils";
import { useStore } from "@/models";
import { observer } from "mobx-react-lite";
import LineChart from "@/components/App/LineChart";
import Star from "../MarketList/star";
import AiSelectNavButton from "@/components/App/AiSelectNavButton";
import { isAiselectEligibleSymbol } from "@/utils/aiSelectEligible";
import ChangePill from "./ChangePill";
import { IconCandlestickChart } from "./icons";
import {
  formatMarketCap,
  formatUsdCompact,
  formatUsdPrice,
  parseSymbolParts,
} from "./utils";
import { resolveAssetFullName } from "./coinFullNames";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { getUrl } = Util;

function isSpotDisplaySymbol(symbol) {
  const s = String(symbol || "");
  return s.length > 0 && !s.toLowerCase().endsWith("_cfd");
}

function isCfdMarketRow(symbol, marketType) {
  const s = String(symbol || "").toLowerCase();
  if (s.endsWith("_cfd")) return true;
  return marketType === "fiat-m" || marketType === "usdt-m";
}

function PcListRow({ marketType, doc, showPerpBadge, marketCapMap, chartData, chartTheme }) {
  const t = useTranslation();
  const { fund, market } = useStore();
  const cfg = market.marketConfig?.[doc.s];
  const { base, quote } = parseSymbolParts(doc.s);
  const isSpotRow = isSpotDisplaySymbol(doc.s);
  const displayBase = fund.getCurrencyDisplayName(base) || base.toUpperCase();
  const displayQuote = fund.getCurrencyDisplayName(quote) || quote.toUpperCase();
  const assetFullName = resolveAssetFullName(base, fund);
  const rawLogo = fund?.currencyMap?.[base]?.logo;
  const showAiSignal = isAiselectEligibleSymbol(doc.s, market.marketConfig);
  const isCfdRow = isCfdMarketRow(doc.s, marketType);
  const isSpotMarketRow = marketType === "spot" && isSpotRow;

  const handleTradeClick = (e) => {
    e.stopPropagation();
    if (isSpotMarketRow) {
      window.location.href = getUrl(getSpotConvertPathForDisplaySymbol(doc.s));
      return;
    }
    if (isCfdRow) {
      window.location.href = getUrl(futuresPathFromSymbol(doc.s));
    }
  };

  return (
    <div className={classNames(styles.tableRow, styles.tableRowStatic)}>
      <div className={styles.nameCell}>
        {marketType !== "spot" && !isSpotRow && (
          <span className={styles.pcStarWrap} onClick={(e) => e.stopPropagation()}>
            <Star marketType={marketType} symbol={doc.s} variant="pc" />
          </span>
        )}
        {rawLogo ? (
          <img src={rawLogo} alt="" className={styles.coinIcon} style={{ width: 32, height: 32 }} />
        ) : (
          <span className={styles.coinIconFallback} style={{ width: 32, height: 32, fontSize: 12 }}>
            {base.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className={styles.nameMeta}>
          <div className={styles.nameTop}>
            <div className={styles.nameLine}>
              <span className={styles.symbolPair}>
                {displayBase}
                <span className={styles.symbolQuote}>/{displayQuote}</span>
              </span>
            </div>
            {showAiSignal && (
              <span className={styles.aiSignalWrap} onClick={(e) => e.stopPropagation()}>
                <AiSelectNavButton symbol={doc.s} aria-label={t("header.aicopy")} />
              </span>
            )}
          </div>
          <div className={styles.symbolSub}>{assetFullName}</div>
        </div>
      </div>
      <div className={styles.priceCell}>{formatUsdPrice(doc.c, cfg?.buyPoint ?? 2)}</div>
      <div className={styles.changeCell}>
        <ChangePill doc={doc} />
      </div>
      <div className={styles.volumeCell}>{formatUsdCompact(doc.v)}</div>
      <div className={styles.marketCapCell}>{formatMarketCap(doc, fund, base, marketCapMap)}</div>
      <div className={styles.actionCell}>
        {chartData?.length ? (
          <div className={styles.sparklineCell} onClick={(e) => e.stopPropagation()}>
            <LineChart
              itemKey={`pc_spark_${doc.s}`}
              chartData={chartData}
              theme={chartTheme}
              width={56}
              height={22}
            />
          </div>
        ) : null}
        {isSpotMarketRow || isCfdRow ? (
          <button
            type="button"
            className={styles.tradeBtn}
            title={t("market.pcTrade") !== "market.pcTrade" ? t("market.pcTrade") : "Trade"}
            aria-label={t("market.pcTrade") !== "market.pcTrade" ? t("market.pcTrade") : "Trade"}
            onClick={handleTradeClick}
          >
            <IconCandlestickChart />
          </button>
        ) : (
          <span
            className={classNames(styles.tradeBtn, styles.tradeBtnDisabled)}
            title={t("market.pcTrade") !== "market.pcTrade" ? t("market.pcTrade") : "Trade"}
            aria-hidden
          >
            <IconCandlestickChart />
          </span>
        )}
      </div>
    </div>
  );
}

export default observer(PcListRow);
